import { Link } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import DateTimePicker from "../components/ui/DateTimePicker";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";
const SAILINGS_API = `${API_BASE}/sailings`;
const BOOKINGS_API = `${API_BASE}/bookings`;
const MANIFEST_API = `${API_BASE}/manifest`;

// Bulk workflow actions — same PATCH endpoints the sailing page uses, so the
// server's own rules apply. `from` lists the statuses an action makes sense
// for; anything else in the selection is skipped rather than attempted.
const BULK_ACTIONS = [
  { action: "open", label: "Open sailing", from: ["draft"] },
  { action: "lock", label: "Lock manifest", from: ["open"] },
  { action: "loading", label: "Start loading", from: ["open"] },
  { action: "depart", label: "Mark departed", from: ["loading"] },
  { action: "arrive", label: "Mark arrived", from: ["departed"] },
  { action: "close", label: "Close sailing", from: ["arrived"] },
];

const statusClasses = {
  open: "bg-emerald-100 text-emerald-700 border border-emerald-200",
  draft: "bg-amber-100 text-amber-700 border border-amber-200",
  confirmed: "bg-sky-100 text-sky-700 border border-sky-200",
  closed: "bg-gray-100 text-gray-700 border border-gray-200",
  departed: "bg-purple-100 text-purple-700 border border-purple-200",
  loading: "bg-blue-100 text-blue-700 border border-blue-200",
  arrived: "bg-teal-100 text-teal-700 border border-teal-200",
  cancelled: "bg-red-100 text-red-700 border border-red-200",
};

const parseETD = (etd) => {
  if (!etd) return new Date();
  const parsed = new Date(etd);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const startOfDay = (value) => {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Whole-day difference (DST safe)
const dayDiff = (a, b) => Math.round((startOfDay(b) - startOfDay(a)) / 86400000);

const formatTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-NZ", { hour: "numeric", minute: "2-digit" });
};

const formatShortDay = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-NZ", { day: "numeric", month: "short" });
};

const barClasses = {
  open: "bg-emerald-600 text-white",
  draft: "bg-amber-500 text-white border border-dashed border-white/70",
  confirmed: "bg-sky-600 text-white",
  closed: "bg-gray-400 text-white",
  departed: "bg-purple-600 text-white",
  loading: "bg-blue-600 text-white",
  arrived: "bg-slate-700 text-white",
  cancelled: "bg-red-400 text-white opacity-60",
};

const formatETDDisplay = (etd) => {
  if (!etd) return "-";

  const date = new Date(etd);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-NZ", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const topStatuses = [
  "active",
  "all",
  "open",
  "draft",
  "confirmed",
  "closed",
  "departed",
  "arrived",
  "cancelled",
];

const Sailings = () => {
  const [viewMode, setViewMode] = useState("table");
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [sailings, setSailings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeStatus, setActiveStatus] = useState("active");
  const [search, setSearch] = useState("");
  const [vesselFilter, setVesselFilter] = useState("all");
  const [routeFilter, setRouteFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedSailings, setSelectedSailings] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const toggleSelect = (id) => {
    setSelectedSailings(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedSailings.size === filteredSailings.length) {
      setSelectedSailings(new Set());
    } else {
      setSelectedSailings(new Set(filteredSailings.map(s => s._id)));
    }
  };

  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState("");
  const bulkMenuRef = useRef(null);

  useEffect(() => {
    if (!bulkMenuOpen) return undefined;
    const onDown = (e) => {
      if (bulkMenuRef.current && !bulkMenuRef.current.contains(e.target)) setBulkMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [bulkMenuOpen]);

  const handleBulkAction = async ({ action, label, from }) => {
    const targets = sailings.filter((s) => selectedSailings.has(s._id));
    const todo = targets.filter((s) => from.includes(String(s.status || "").toLowerCase()));
    const skipped = targets.length - todo.length;

    if (todo.length === 0) {
      setBulkMenuOpen(false);
      setBulkResult(`"${label}" doesn't apply to the selected sailings (needs status: ${from.join(" / ")}).`);
      return;
    }

    const ok = window.confirm(
      `${label} for ${todo.length} sailing${todo.length > 1 ? "s" : ""}?` +
      (skipped ? `\n${skipped} will be skipped (wrong status).` : "")
    );
    if (!ok) return;

    setBulkMenuOpen(false);
    setBulkBusy(true);
    setBulkResult("");
    const token = localStorage.getItem("token");
    const failed = [];

    await Promise.all(todo.map(async (s) => {
      try {
        const res = await fetch(`${MANIFEST_API}/${s._id}/${action}`, {
          method: "PATCH",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) {
          const text = await res.text();
          let msg = text;
          try { const j = JSON.parse(text); msg = j.message || j.error || text; } catch (e) { /* plain text */ }
          throw new Error(msg || `HTTP ${res.status}`);
        }
      } catch (err) {
        failed.push({ ref: s.sailingRef || s._id?.slice(-6).toUpperCase(), reason: err.message });
      }
    }));

    const done = todo.length - failed.length;
    setBulkResult(
      `${label}: ${done} sailing${done === 1 ? "" : "s"} updated.` +
      (skipped ? ` ${skipped} skipped.` : "") +
      (failed.length ? ` Could not update — ${failed.slice(0, 3).map((f) => `${f.ref}: ${f.reason}`).join(" | ")}${failed.length > 3 ? ` (+${failed.length - 3} more)` : ""}` : "")
    );
    setSelectedSailings(new Set());
    setBulkBusy(false);
    await fetchData();
  };

  const handleDeleteSelected = async () => {
    if (selectedSailings.size === 0) return;
    const confirmed = window.confirm(`Delete ${selectedSailings.size} sailing${selectedSailings.size > 1 ? "s" : ""}? This cannot be undone.`);
    if (!confirmed) return;
    try {
      setDeleting(true);
      const token = localStorage.getItem("token");
      await Promise.all([...selectedSailings].map(id =>
        fetch(`${SAILINGS_API}/${id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        })
      ));
      setSelectedSailings(new Set());
      await fetchData();
    } catch (err) {
      console.error(err);
      alert("Failed to delete some sailings.");
    } finally {
      setDeleting(false);
    }
  };

  // Click-to-edit save for ETD / ETA / booking cut-off straight from the list.
  const saveSailingDate = async (sailing, field, localValue) => {
    const iso = localValue ? new Date(localValue).toISOString() : null;
    const token = localStorage.getItem("token");
    const response = await fetch(`${SAILINGS_API}/${sailing._id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ ...sailing, etdDate: undefined, [field]: iso }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to save");
    }
    setSailings((prev) =>
      prev.map((item) => (item._id === sailing._id ? { ...item, [field]: iso } : item))
    );
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      const [sailingsRes, bookingsRes] = await Promise.all([
        fetch(SAILINGS_API),
        fetch(BOOKINGS_API),
      ]);

      const sailingsData = await sailingsRes.json();
      const bookingsData = await bookingsRes.json();

      setSailings(Array.isArray(sailingsData) ? sailingsData : []);
      setBookings(Array.isArray(bookingsData) ? bookingsData : []);
    } catch (error) {
      console.error("Failed to fetch sailings:", error);
      setSailings([]);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getSailingBookings = (sailingId) => {
    return bookings.filter((booking) => {
      const bookingSailingId =
        booking.sailing?._id || booking.sailing || booking.sailingId;

      return (
        String(bookingSailingId) === String(sailingId) &&
        booking.status !== "cancelled"
      );
    });
  };

  const calculateStats = (sailing) => {
    const linkedBookings = getSailingBookings(sailing._id);

    const bookedWeight = linkedBookings.reduce(
      (sum, booking) => sum + Number(booking.totals?.weightKg || 0),
      0
    );

    const bookedCube = linkedBookings.reduce(
      (sum, booking) => sum + Number(booking.totals?.cubeM3 || 0),
      0
    );

    const bookedLivestock = linkedBookings.reduce(
      (sum, booking) => sum + Number(booking.totals?.livestockHead || 0),
      0
    );

    const maxWeight = Number(sailing.capacity?.maxWeightKg || 0);
    const maxCube = Number(sailing.capacity?.maxCubeM3 || 0);
    const maxLivestock = Number(sailing.capacity?.maxLivestockHead || 0);

    const weightPct = maxWeight
      ? Math.min(Math.round((bookedWeight / maxWeight) * 100), 100)
      : 0;

    const cubePct = maxCube
      ? Math.min(Math.round((bookedCube / maxCube) * 100), 100)
      : 0;

    const livestockPct = maxLivestock
      ? Math.min(Math.round((bookedLivestock / maxLivestock) * 100), 100)
      : 0;

    const overallPct = Math.round((weightPct + cubePct + livestockPct) / 3);

    return {
      bookingCount: linkedBookings.length,
      bookedWeight,
      bookedCube,
      bookedLivestock,
      maxWeight,
      maxCube,
      maxLivestock,
      weightPct,
      cubePct,
      livestockPct,
      overallPct,
    };
  };

  const sailingsWithDates = useMemo(() => {
    return sailings.map((sailing) => ({
      ...sailing,
      etdDate: parseETD(sailing.etd),
    }));
  }, [sailings]);

  const vesselOptions = useMemo(() => {
    const values = sailingsWithDates
      .map(
        (item) =>
          item.vesselName ||
          item.vessel?.name ||
          item.vessel ||
          item.vesselLabel ||
          ""
      )
      .filter(Boolean);

    return [...new Set(values)].sort((a, b) => String(a).localeCompare(String(b)));
  }, [sailingsWithDates]);

  const routeOptions = useMemo(() => {
    const values = sailingsWithDates
      .map(
        (item) =>
          item.routeLabel || `${item.origin || "-"} → ${item.destination || "-"}`
      )
      .filter(Boolean);

    return [...new Set(values)].sort((a, b) => String(a).localeCompare(String(b)));
  }, [sailingsWithDates]);

  const filteredSailings = useMemo(() => {
    return sailingsWithDates.filter((sailing) => {
      const sailingRef =
        sailing.sailingRef || sailing._id?.slice(-6).toUpperCase() || "";

      const vessel =
        sailing.vesselName ||
        sailing.vessel?.name ||
        sailing.vessel ||
        sailing.vesselLabel ||
        "";

      const route =
        sailing.routeLabel || `${sailing.origin || "-"} → ${sailing.destination || "-"}`;

      const status = String(sailing.status || "").toLowerCase();

      const matchesSearch =
        search.trim() === "" ||
        String(sailingRef).toLowerCase().includes(search.toLowerCase()) ||
        String(vessel).toLowerCase().includes(search.toLowerCase()) ||
        String(route).toLowerCase().includes(search.toLowerCase()) ||
        String(sailing.origin || "").toLowerCase().includes(search.toLowerCase()) ||
        String(sailing.destination || "").toLowerCase().includes(search.toLowerCase());

      const matchesTopStatus =
        activeStatus === "all" ||
        (activeStatus === "active" ? status !== "closed" : status === activeStatus);

      const matchesVessel =
        vesselFilter === "all" || String(vessel) === vesselFilter;

      const matchesRoute =
        routeFilter === "all" || String(route) === routeFilter;

      const matchesStatus =
        statusFilter === "all" || status === statusFilter;

      return (
        matchesSearch &&
        matchesTopStatus &&
        matchesVessel &&
        matchesRoute &&
        matchesStatus
      );
    });
  }, [
    sailingsWithDates,
    search,
    activeStatus,
    vesselFilter,
    routeFilter,
    statusFilter,
  ]);

  const calendarWeeks = useMemo(() => {
    const y = currentMonth.getFullYear();
    const m = currentMonth.getMonth();
    let offset = new Date(y, m, 1).getDay();
    offset = offset === 0 ? 6 : offset - 1;
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const weekCount = Math.ceil((offset + daysInMonth) / 7);
    const now = startOfDay(new Date());

    return Array.from({ length: weekCount }, (_, w) => {
      const days = Array.from({ length: 7 }, (_, i) => {
        const date = new Date(y, m, 1 - offset + w * 7 + i);
        return {
          date,
          day: date.getDate(),
          inMonth: date.getMonth() === m,
          isToday: dayDiff(now, date) === 0,
        };
      });
      const weekStart = days[0].date;
      const weekEnd = days[6].date;

      // Voyage bars that touch this week
      const bars = [];
      filteredSailings.forEach((sailing) => {
        if (!sailing.etd) return;
        const start = startOfDay(parseETD(sailing.etd));
        let end = sailing.eta ? startOfDay(new Date(sailing.eta)) : start;
        if (Number.isNaN(end.getTime()) || end < start) end = start;
        if (end < weekStart || start > weekEnd) return;
        bars.push({
          sailing,
          start,
          total: dayDiff(start, end) + 1,
          startCol: Math.max(dayDiff(weekStart, start), 0),
          endCol: Math.min(dayDiff(weekStart, end), 6),
          contLeft: start < weekStart,
          contRight: end > weekEnd,
        });
      });
      bars.sort((a, b) => a.startCol - b.startCol || b.endCol - a.endCol);
      const laneEnds = [];
      bars.forEach((bar) => {
        let lane = laneEnds.findIndex((end) => end < bar.startCol);
        if (lane === -1) {
          lane = laneEnds.length;
          laneEnds.push(bar.endCol);
        } else {
          laneEnds[lane] = bar.endCol;
        }
        bar.lane = lane;
      });

      // Booking cut-off markers that fall in this week
      const perCol = Array(7).fill(0);
      const cutoffs = [];
      filteredSailings.forEach((sailing) => {
        if (!sailing.bookingCutoff) return;
        const when = new Date(sailing.bookingCutoff);
        if (Number.isNaN(when.getTime())) return;
        const d = startOfDay(when);
        if (d < weekStart || d > weekEnd) return;
        const col = dayDiff(weekStart, d);
        cutoffs.push({ sailing, when, col, row: perCol[col] });
        perCol[col] += 1;
      });

      return { days, bars, cutoffs, cutoffRows: Math.max(...perCol) };
    });
  }, [currentMonth, filteredSailings]);

  // Headline numbers for whatever voyages touch the month on screen
  const monthStats = useMemo(() => {
    const y = currentMonth.getFullYear();
    const m = currentMonth.getMonth();
    const monthStart = new Date(y, m, 1);
    const monthEnd = new Date(y, m + 1, 0);
    let voyages = 0, seaDays = 0, bookingCount = 0, head = 0, weightKg = 0, pctSum = 0;
    filteredSailings.forEach((sailing) => {
      if (!sailing.etd) return;
      const start = startOfDay(parseETD(sailing.etd));
      let end = sailing.eta ? startOfDay(new Date(sailing.eta)) : start;
      if (Number.isNaN(end.getTime()) || end < start) end = start;
      if (end < monthStart || start > monthEnd) return;
      const stats = calculateStats(sailing);
      voyages += 1;
      seaDays += dayDiff(start, end) + 1;
      bookingCount += stats.bookingCount;
      head += stats.bookedLivestock;
      weightKg += stats.bookedWeight;
      pctSum += stats.overallPct;
    });
    return { voyages, seaDays, bookingCount, head, weightKg, avgPct: voyages ? Math.round(pctSum / voyages) : 0 };
  }, [currentMonth, filteredSailings, bookings]);

  const handleToday = () => setCurrentMonth(new Date());

  const handlePrevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  const handleNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  return (
    <div className="w-full p-6 space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sailings</h1>
          <p className="text-sm text-gray-500 mt-1">
            View, search and manage scheduled sailings and capacity.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/dashboard/sailings/schedule/print"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Print Schedule
          </Link>

          <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1">
          <button
            onClick={() => setViewMode("table")}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              viewMode === "table"
                ? "bg-black text-white"
                : "text-gray-700 hover:bg-gray-100"
            }`}
          >
            Table
          </button>

          <button
            onClick={() => setViewMode("calendar")}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              viewMode === "calendar"
                ? "bg-black text-white"
                : "text-gray-700 hover:bg-gray-100"
            }`}
          >
            Calendar
          </button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {topStatuses.map((status) => (
          <button
            key={status}
            onClick={() => setActiveStatus(status)}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              activeStatus === status
                ? "bg-black text-white"
                : "border border-gray-300 bg-white hover:bg-gray-50"
            }`}
          >
            {status === "all" ? "All Sailings" : status === "active" ? "Active" : toTitle(status)}
          </button>
        ))}
      </div>

      <div className="border border-gray-300 rounded-xl bg-white p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <input
            type="text"
            placeholder="Search ref, route, vessel..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          />

          <select
            value={vesselFilter}
            onChange={(e) => setVesselFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="all">All Vessels</option>
            {vesselOptions.map((vessel) => (
              <option key={vessel} value={vessel}>
                {vessel}
              </option>
            ))}
          </select>

          <select
            value={routeFilter}
            onChange={(e) => setRouteFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="all">All Routes</option>
            {routeOptions.map((route) => (
              <option key={route} value={route}>
                {route}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="all">All Statuses</option>
            <option value="open">Open</option>
            <option value="draft">Draft</option>
            <option value="loading">Loading</option>
            <option value="departed">Departed</option>
            <option value="arrived">Arrived</option>
            <option value="closed">Closed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {viewMode === "table" ? (
        <div className="border border-gray-300 rounded-xl bg-white">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Sailing List</h2>
              <p className="text-sm text-gray-500">
                Showing {filteredSailings.length} of {sailings.length} sailings.
                {selectedSailings.size > 0 && <span className="ml-2 font-semibold text-slate-700">{selectedSailings.size} selected.</span>}
              </p>
            </div>
            {selectedSailings.size > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelectedSailings(new Set())}
                  className="px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-900"
                >
                  Clear
                </button>

                <div className="relative" ref={bulkMenuRef}>
                  <button
                    type="button"
                    disabled={bulkBusy || deleting}
                    onClick={() => setBulkMenuOpen((o) => !o)}
                    className="rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-50"
                  >
                    {bulkBusy ? "Updating..." : `Actions (${selectedSailings.size}) ▾`}
                  </button>

                  {bulkMenuOpen && (
                    <div className="absolute right-0 z-20 mt-2 w-64 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
                      <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                        Apply to selected
                      </div>
                      {BULK_ACTIONS.map((a) => (
                        <button
                          key={a.action}
                          type="button"
                          onClick={() => handleBulkAction(a)}
                          className="block w-full rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                        >
                          {a.label}
                        </button>
                      ))}

                      <div className="my-1 border-t border-slate-100" />
                      <button type="button" onClick={() => { setBulkMenuOpen(false); handleDeleteSelected(); }}
                        className="block w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-red-700 hover:bg-red-50">
                        Delete selected…
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {bulkResult && (
            <div className="flex items-start justify-between gap-4 border-b border-gray-200 bg-slate-50 px-5 py-3 text-sm text-slate-700">
              <span>{bulkResult}</span>
              <button type="button" onClick={() => setBulkResult("")} className="text-slate-400 hover:text-slate-700">×</button>
            </div>
          )}

          <div className="overflow-x-auto rounded-b-xl">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-300">
                <tr className="text-left">
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={filteredSailings.length > 0 && selectedSailings.size === filteredSailings.length}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                  </th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Sailing Ref</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Route</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Booking Cut-off</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">ETD</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">ETA</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Bookings</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Capacity</th>
                  <th className="px-4 py-3 font-semibold text-gray-700 text-right">View</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="px-4 py-8 text-center text-gray-500">Loading sailings...</td>
                  </tr>
                ) : filteredSailings.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-4 py-8 text-center text-gray-500">No sailings match those filters.</td>
                  </tr>
                ) : (
                  filteredSailings.map((sailing) => {
                    const stats = calculateStats(sailing);
                    return (
                      <tr
                        key={sailing._id}
                        className={`border-t border-gray-200 hover:bg-gray-50 ${selectedSailings.has(sailing._id) ? "bg-slate-50" : ""}`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedSailings.has(sailing._id)}
                            onChange={() => toggleSelect(sailing._id)}
                            className="h-4 w-4 rounded border-gray-300"
                          />
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {sailing.sailingRef || sailing._id?.slice(-6).toUpperCase()}
                        </td>
                        <td className="px-4 py-3">
                          {sailing.routeLabel || `${sailing.origin || "-"} → ${sailing.destination || "-"}`}
                        </td>
                        <td className="px-4 py-3">
                          <EditableDateCell title={`${sailing.sailingRef || sailing._id?.slice(-6).toUpperCase()} · Booking cut-off`} value={sailing.bookingCutoff} onSave={(v) => saveSailingDate(sailing, "bookingCutoff", v)} />
                        </td>
                        <td className="px-4 py-3">
                          <EditableDateCell title={`${sailing.sailingRef || sailing._id?.slice(-6).toUpperCase()} · ETD`} value={sailing.etd} onSave={(v) => saveSailingDate(sailing, "etd", v)} />
                        </td>
                        <td className="px-4 py-3">
                          <EditableDateCell title={`${sailing.sailingRef || sailing._id?.slice(-6).toUpperCase()} · ETA`} value={sailing.eta} onSave={(v) => saveSailingDate(sailing, "eta", v)} />
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${statusClasses[sailing.status] || "bg-gray-100 text-gray-700 border border-gray-200"}`}>
                            {toTitle(sailing.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3">{stats.bookingCount}</td>
                        <td className="px-4 py-3 min-w-[220px]">
                          <div className="space-y-2">
                            <MiniBar label="Weight" value={`${stats.weightPct}%`} width={stats.weightPct} />
                            <MiniBar label="Livestock" value={`${stats.livestockPct}%`} width={stats.livestockPct} />
                            <MiniBar label="Cube" value={`${stats.cubePct}%`} width={stats.cubePct} />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Link
                            to={`/dashboard/sailings/${sailing._id}`}
                            className="border border-gray-300 px-3 py-1 rounded-lg text-xs font-medium hover:bg-gray-100 inline-block"
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <SailingCalendar
          currentMonth={currentMonth}
          weeks={calendarWeeks}
          monthStats={monthStats}
          calculateStats={calculateStats}
          onPrev={handlePrevMonth}
          onNext={handleNextMonth}
          onToday={handleToday}
        />
      )}

      <div className="flex justify-end">
        <Link
          to="/dashboard/sailings/new"
          className="bg-black text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800"
        >
          + New Sailing
        </Link>
      </div>
    </div>
  );
};

function toTitle(value) {
  if (!value) return "";

  return String(value)
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function toLocalInputValue(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function DateEditModal({ title, value, onSave, onClose }) {
  const [draft, setDraft] = useState(toLocalInputValue(value));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    try {
      setSaving(true);
      setError("");
      await onSave(draft);
      onClose();
    } catch (err) {
      setError(err.message || "Could not save");
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-start justify-center bg-black/40 px-4 pt-[12vh]"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        <div className="mt-3">
          <DateTimePicker value={draft} onChange={(val) => setDraft(val)} placeholder="Select date & time" />
        </div>
        {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
        <div className="mt-[22rem] flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={saving}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            Cancel
          </button>
          <button type="button" onClick={save} disabled={saving}
            className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Table cell showing a date/time; click to edit in a pop-up (avoids the table clipping the picker).
function EditableDateCell({ title, value, onSave }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Click to edit"
        className="whitespace-nowrap rounded-md px-1.5 py-1 -mx-1.5 text-left text-gray-800 hover:bg-gray-100 transition"
      >
        {value ? formatETDDisplay(value) : <span className="text-gray-400">Set date</span>}
      </button>
      {open && <DateEditModal title={title} value={value} onSave={onSave} onClose={() => setOpen(false)} />}
    </>
  );
}

function SailingCalendar({ currentMonth, weeks, monthStats, calculateStats, onPrev, onNext, onToday }) {
  const tiles = [
    { label: "Voyages", value: monthStats.voyages },
    { label: "Days at sea", value: monthStats.seaDays },
    { label: "Bookings", value: monthStats.bookingCount },
    { label: "Livestock", value: `${monthStats.head.toLocaleString()} hd` },
    { label: "Weight", value: `${(monthStats.weightKg / 1000).toFixed(1)} t` },
    { label: "Avg capacity", value: `${monthStats.avgPct}%` },
  ];

  const legend = [
    ["open", "Open"], ["draft", "Draft"], ["confirmed", "Confirmed"], ["loading", "Loading"],
    ["departed", "Departed"], ["arrived", "Arrived"], ["closed", "Closed"],
  ];

  return (
    <div className="border border-gray-300 rounded-xl bg-white overflow-hidden shadow-sm">
      {/* Header */}
      <div className="flex flex-col gap-3 border-b border-gray-200 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-4 py-4 text-white lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-2">
          <button onClick={onPrev} className="rounded-lg border border-white/20 px-3 py-2 text-sm font-medium hover:bg-white/10">‹ Prev</button>
          <button onClick={onToday} className="rounded-lg border border-white/20 px-3 py-2 text-sm font-medium hover:bg-white/10">Today</button>
          <button onClick={onNext} className="rounded-lg border border-white/20 px-3 py-2 text-sm font-medium hover:bg-white/10">Next ›</button>
        </div>
        <h2 className="text-xl font-bold tracking-wide">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h2>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
          {tiles.map((tile) => (
            <div key={tile.label} className="rounded-lg bg-white/10 px-3 py-1.5 text-center">
              <div className="text-sm font-bold leading-tight">{tile.value}</div>
              <div className="text-[10px] uppercase tracking-wide text-white/60">{tile.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-gray-200 bg-gray-50 px-4 py-2 text-[11px] text-gray-600">
        {legend.map(([key, label]) => (
          <span key={key} className="inline-flex items-center gap-1.5">
            <span className={`inline-block h-2.5 w-2.5 rounded-sm ${barClasses[key].split(" ")[0]}`} />
            {label}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block rounded border border-rose-200 bg-rose-50 px-1 text-[9px] font-bold text-rose-700">CUT-OFF</span>
          Booking cut-off
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="relative inline-flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-500" />
          </span>
          Today at sea
        </span>
      </div>

      {/* Weekday names */}
      <div className="grid grid-cols-7 border-b border-gray-300 bg-gray-50">
        {dayNames.map((day) => (
          <div key={day} className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600 border-r last:border-r-0 border-gray-200">
            {day}
          </div>
        ))}
      </div>

      {/* Weeks */}
      {weeks.map((week, wi) => (
        <div key={wi} className="relative border-b border-gray-200 last:border-b-0">
          {/* column backgrounds */}
          <div className="pointer-events-none absolute inset-0 grid grid-cols-7">
            {week.days.map((d, di) => (
              <div
                key={di}
                className={`border-r border-gray-200 last:border-r-0 ${
                  d.isToday ? "bg-amber-50" : d.inMonth ? "bg-white" : "bg-gray-50"
                }`}
              />
            ))}
          </div>

          {/* day numbers */}
          <div className="relative grid grid-cols-7">
            {week.days.map((d, di) => (
              <div key={di} className="px-2 pt-1.5">
                <span
                  className={`inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full px-1 text-xs font-semibold ${
                    d.isToday ? "bg-black text-white" : d.inMonth ? "text-gray-800" : "text-gray-400"
                  }`}
                >
                  {d.day}
                </span>
              </div>
            ))}
          </div>

          {/* cut-offs + voyage bars */}
          <div
            className="relative px-1 pb-2 pt-1"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
              rowGap: 4,
              minHeight: 64,
            }}
          >
            {week.cutoffs.map((c) => {
              const passed = c.when < new Date();
              const ref = c.sailing.sailingRef || c.sailing._id?.slice(-6).toUpperCase();
              return (
                <Link
                  key={`co-${c.sailing._id}`}
                  to={`/dashboard/sailings/${c.sailing._id}`}
                  title={`Booking cut-off for ${ref}: ${formatETDDisplay(c.sailing.bookingCutoff)}${passed ? " (passed)" : ""}`}
                  style={{ gridColumn: `${c.col + 1} / ${c.col + 2}`, gridRow: c.row + 1 }}
                  className={`mx-0.5 flex items-center gap-1 truncate rounded border px-1.5 py-0.5 text-[10px] font-semibold ${
                    passed
                      ? "border-gray-200 bg-gray-100 text-gray-500 line-through"
                      : "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                  }`}
                >
                  <span className="font-bold">CUT-OFF</span>
                  <span className="truncate">{ref} · {formatTime(c.when)}</span>
                </Link>
              );
            })}

            {week.bars.map((bar) => {
              const { sailing } = bar;
              const stats = calculateStats(sailing);
              const status = String(sailing.status || "").toLowerCase();
              const ref = sailing.sailingRef || sailing._id?.slice(-6).toUpperCase();
              const route = sailing.routeLabel || `${sailing.origin || "-"} → ${sailing.destination || "-"}`;
              const vessel = sailing.vesselName || sailing.vessel?.name || (typeof sailing.vessel === "string" ? sailing.vessel : "") || "Vessel";
              const cols = bar.endCol - bar.startCol + 1;
              const tonnes = (stats.bookedWeight / 1000).toFixed(1);

              return (
                <Link
                  key={`bar-${sailing._id}`}
                  to={`/dashboard/sailings/${sailing._id}`}
                  title={`${ref} · ${route}\n${vessel}\nETD ${formatETDDisplay(sailing.etd)}\nETA ${formatETDDisplay(sailing.eta)}\n${stats.bookingCount} bookings · ${stats.bookedLivestock} hd · ${tonnes} t · ${stats.overallPct}% capacity`}
                  style={{
                    gridColumn: `${bar.startCol + 1} / ${bar.endCol + 2}`,
                    gridRow: week.cutoffRows + bar.lane + 1,
                  }}
                  className={`mx-0.5 flex min-w-0 flex-col overflow-hidden shadow-sm transition hover:brightness-110 hover:shadow-md ${
                    barClasses[status] || "bg-gray-500 text-white"
                  } ${bar.contLeft ? "rounded-l-none" : "rounded-l-xl"} ${bar.contRight ? "rounded-r-none" : "rounded-r-xl"}`}
                >
                  {/* title line */}
                  <div className="flex items-center gap-1.5 px-2 pt-1.5 text-[11px] leading-tight">
                    {bar.contLeft && <span className="opacity-70">◀</span>}
                    <span className="font-bold">{ref}</span>
                    <span className="truncate opacity-90">{route}</span>
                    <span className="ml-auto hidden shrink-0 rounded bg-black/20 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide sm:inline">
                      {vessel}
                    </span>
                    {bar.contRight && <span className="opacity-70">▶</span>}
                  </div>

                  {/* load line */}
                  {cols >= 2 && (
                    <div className="px-2 pt-0.5 text-[10px] opacity-90">
                      {stats.bookingCount} bkg{stats.bookingCount === 1 ? "" : "s"}
                      {stats.bookedLivestock > 0 && ` · ${stats.bookedLivestock} hd`}
                      {stats.bookedWeight > 0 && ` · ${tonnes} t`}
                      {stats.bookedCube > 0 && ` · ${Math.round(stats.bookedCube)} m³`}
                    </div>
                  )}

                  {/* one segment per day: depart / at sea / arrive */}
                  <div className="mt-1 flex">
                    {Array.from({ length: cols }, (_, ci) => {
                      const col = bar.startCol + ci;
                      const dayIdx = dayDiff(bar.start, week.days[col].date) + 1;
                      const isFirst = dayIdx === 1;
                      const isLast = dayIdx === bar.total;
                      const isToday = week.days[col].isToday;
                      let label;
                      if (bar.total === 1) label = `${formatTime(sailing.etd)} → ${formatTime(sailing.eta || sailing.etd)}`;
                      else if (isFirst) label = `Depart ${formatTime(sailing.etd)}`;
                      else if (isLast) label = `Arrive ${formatTime(sailing.eta)}`;
                      else label = `At sea · Day ${dayIdx}/${bar.total}`;
                      return (
                        <div
                          key={ci}
                          className={`relative flex min-w-0 flex-1 items-center justify-center gap-1 border-t border-white/20 px-1 py-1 text-[9px] font-semibold uppercase tracking-wide ${
                            ci > 0 ? "border-l border-l-white/20" : ""
                          } ${isFirst || isLast ? "bg-black/25" : "bg-black/10"}`}
                        >
                          {isToday && (
                            <span className="relative inline-flex h-2 w-2 shrink-0">
                              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-300 opacity-80" />
                              <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-300" />
                            </span>
                          )}
                          <span className="truncate">{label}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* capacity */}
                  <div className="flex items-center gap-2 px-2 pb-1.5 pt-1">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-black/25">
                      <div className="h-1.5 rounded-full bg-white" style={{ width: `${stats.overallPct}%` }} />
                    </div>
                    <span className="text-[10px] font-bold">{stats.overallPct}%</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function MiniBar({ label, value, width }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="font-medium text-gray-900">{value}</span>
      </div>
      <div className="h-2 bg-gray-200 rounded overflow-hidden">
        <div
          className="h-2 bg-black rounded"
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}

export default Sailings;